import {
  replaceCanvasesWithImages,
  waitForImageLoad,
} from '@client/helpers/canvas-snapshot.helpers';
import { injectCoverMapDataUrl } from '@shared/helpers/report-pdf-cover-map-inject.helpers';

export { injectCoverMapDataUrl };

/**
 * Set on `<html>` by PrintView when it is safe to call `page.pdf()`.
 * Browserless waits on this. Cover slots must be print-safe first: an idle
 * Mapbox canvas replaced by a PNG, a skipped map (no token / stored image),
 * or live WebGL discarded after the cover wait so a failed/slow attach cannot
 * block the export. Chrome's print pipeline does not rasterize WebGL.
 */
export const REPORT_PDF_READY_ATTR = 'data-report-pdf-ready';

/**
 * Set on `<html>` by the cover page when Mapbox has gone idle, or when there
 * is no map to wait for (no token). Does **not** mean "safe to PDF" yet —
 * PrintView snapshots the canvas after this, then sets {@link REPORT_PDF_READY_ATTR}.
 */
export const REPORT_COVER_MAP_READY_ATTR = 'data-report-cover-map-ready';

/** Marks the cover map slot so print snapshot only replaces that WebGL canvas. */
export const REPORT_COVER_MAP_SLOT_ATTR = 'data-report-cover-map';

type WaitUntilOptions = {
  timeoutMs: number;
  intervalMs?: number;
  signal?: { cancelled: boolean };
};

/**
 * Polls `predicate` until it is true, the timeout elapses, or `signal.cancelled`.
 * Resolves `true` when the predicate passed, otherwise `false`.
 */
export const waitUntil = (
  predicate: () => boolean,
  options: WaitUntilOptions,
): Promise<boolean> => {
  const intervalMs = options.intervalMs ?? 100;
  return new Promise((resolve) => {
    if (predicate()) {
      resolve(true);
      return;
    }
    const startedAt = Date.now();
    const id = window.setInterval(() => {
      if (options.signal?.cancelled) {
        window.clearInterval(id);
        resolve(false);
        return;
      }
      if (predicate()) {
        window.clearInterval(id);
        resolve(true);
        return;
      }
      if (Date.now() - startedAt >= options.timeoutMs) {
        window.clearInterval(id);
        resolve(false);
      }
    }, intervalMs);
  });
};

/**
 * True when the print report has mounted at least `minPages` page roots.
 */
export const hasMinimumReportPages = (
  root: ParentNode,
  minPages: number,
): boolean => root.querySelectorAll('[data-report-page]').length >= minPages;

/**
 * True when the cover map has reached Mapbox idle (or there is no map to wait for).
 */
export const isCoverMapReady = (documentElement: Element): boolean =>
  documentElement.hasAttribute(REPORT_COVER_MAP_READY_ATTR);

/**
 * Signals that the cover 3D map has painted, or that there is no map to wait
 * for. PrintView still has to snapshot before setting {@link REPORT_PDF_READY_ATTR}.
 */
export const markCoverMapReady = (documentElement: Element): void => {
  documentElement.setAttribute(REPORT_COVER_MAP_READY_ATTR, 'true');
};

/**
 * Clears {@link REPORT_COVER_MAP_READY_ATTR} so a later Mapbox idle is required.
 * Used when storeys change after a premature empty-map idle.
 */
export const clearCoverMapReady = (documentElement: Element): void => {
  documentElement.removeAttribute(REPORT_COVER_MAP_READY_ATTR);
};

/**
 * Replaces WebGL canvases in cover-map slots with PNGs. Scoped to
 * {@link REPORT_COVER_MAP_SLOT_ATTR} so Chart.js 2d canvases on later pages
 * are not torn out — those usually print; Mapbox/three.js WebGL does not.
 */
export const snapshotCoverMapCanvases = async (
  root: ParentNode,
): Promise<void> => {
  const slots = root.querySelectorAll(`[${REPORT_COVER_MAP_SLOT_ATTR}]`);
  const images = [...slots].flatMap((slot) => replaceCanvasesWithImages(slot));
  await Promise.all(images.map(waitForImageLoad));
};

/**
 * Removes leftover WebGL canvases from cover-map slots so Chrome is not asked
 * to print a live Mapbox canvas.
 */
export const discardCoverMapCanvases = (root: ParentNode): void => {
  for (const slot of root.querySelectorAll(`[${REPORT_COVER_MAP_SLOT_ATTR}]`)) {
    for (const canvas of [...slot.querySelectorAll('canvas')]) {
      canvas.remove();
    }
  }
};

/**
 * Moves PNG data-URL snapshots to the cover slot so PrintView can unmount
 * Map3d without deleting the image that replaced the WebGL canvas.
 */
export const hoistCoverSnapshotImages = (root: ParentNode): void => {
  for (const slot of root.querySelectorAll(`[${REPORT_COVER_MAP_SLOT_ATTR}]`)) {
    for (const img of [...slot.querySelectorAll('img')]) {
      if (img.parentElement === slot) continue;
      if (!img.src.startsWith('data:image')) continue;
      slot.appendChild(img);
    }
  }
};

/**
 * True when no live canvas remains in cover-map slots.
 */
export const hasNoCoverMapCanvas = (root: ParentNode): boolean =>
  [...root.querySelectorAll(`[${REPORT_COVER_MAP_SLOT_ATTR}]`)].every(
    (slot) => slot.querySelector('canvas') === null,
  );

/**
 * Makes cover-map slots print-safe so PrintView can set
 * {@link REPORT_PDF_READY_ATTR}. Snapshots an idle map; if the map never
 * idled or the snapshot cannot be read, live canvases are removed so the PDF
 * can still complete (the cover slot may be empty). Snapshot images are
 * hoisted onto the slot so Map3d can unmount afterwards.
 */
export const finalizeCoverMapForPdf = async (
  root: ParentNode,
  documentElement: Element,
): Promise<void> => {
  try {
    if (isCoverMapReady(documentElement)) {
      await snapshotCoverMapCanvases(root);
    }
  } catch {
    // Snapshot errors must not block PDF export.
  }
  hoistCoverSnapshotImages(root);
  discardCoverMapCanvases(root);
};
